import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Spinner from 'src/components/Spinner';
import {
    IWorkspaceSetting,
    IWorkspaceSettingOption,
} from 'src/hooks/useWorkspaceSettings';

export interface IDatadogCredentialValues {
    apiKey: string
    site: string
    service: string
}

export interface IAddDatadogCredentialModalProps {
    isOpen: boolean
    apiKeyDefinition?: IWorkspaceSetting
    siteDefinition?: IWorkspaceSetting
    serviceDefinition?: IWorkspaceSetting
    handleClose: () => void
    handleValidate: (values: IDatadogCredentialValues) => Promise<{ ok: boolean; message: string }>
    handleSaved: (values: IDatadogCredentialValues) => void
}

const AddDatadogCredentialModal: FunctionComponent<IAddDatadogCredentialModalProps> = ({
    isOpen,
    apiKeyDefinition,
    siteDefinition,
    serviceDefinition,
    handleClose,
    handleValidate,
    handleSaved,
}) => {
    const siteOptions: IWorkspaceSettingOption[] = siteDefinition?.options || []
    const defaultSite = siteDefinition?.defaultValue || siteOptions[0]?.value || 'EU1'
    const defaultService = serviceDefinition?.defaultValue || 'meltano'

    const [apiKey, setApiKey] = useState('')
    const [site, setSite] = useState(defaultSite)
    const [service, setService] = useState(defaultService)
    const [isValidating, setIsValidating] = useState(false)
    const [apiKeyError, setApiKeyError] = useState<string | undefined>(undefined)

    useEffect(() => {
        if (isOpen) {
            setApiKey('')
            setSite(defaultSite)
            setService(defaultService)
            setApiKeyError(undefined)
            setIsValidating(false)
        }
    }, [isOpen, defaultSite, defaultService])

    const handleSave = async () => {
        if (!apiKey || !apiKey.trim()) {
            setApiKeyError('API key is required')
            return
        }
        setApiKeyError(undefined)
        setIsValidating(true)
        try {
            const result = await handleValidate({ apiKey, site, service })
            if (result.ok) {
                handleSaved({ apiKey, site, service })
                handleClose()
            } else {
                setApiKeyError(result.message || 'Validation failed')
            }
        } catch (e: any) {
            setApiKeyError('Validation request failed')
        } finally {
            setIsValidating(false)
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Add a Datadog credential</Typography>
                <IconButton
                    aria-label="Close"
                    size="small"
                    onClick={handleClose}
                    disabled={isValidating}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <div className="field" style={{ marginTop: 8, marginBottom: 16 }}>
                    <Typography variant="subtitle2">{apiKeyDefinition?.label || 'Datadog API Key'}</Typography>
                    <Typography variant="caption" color="textSecondary" component="p">
                        Generate and paste your API key here. This API key will be masked and can only be seen one-time.
                    </Typography>
                    <TextField
                        data-testid="datadog-api-key"
                        autoFocus
                        fullWidth
                        required
                        variant="outlined"
                        label=""
                        type="password"
                        value={apiKey}
                        error={!!apiKeyError}
                        helperText={apiKeyError}
                        onChange={e => setApiKey(e.target.value)}
                    />
                </div>
                <div className="field" style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Datadog site</Typography>
                    <TextField
                        data-testid="datadog-site"
                        select
                        fullWidth
                        variant="outlined"
                        label=""
                        value={site}
                        onChange={e => setSite(e.target.value)}
                    >
                        {siteOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label || opt.value}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className="field" style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Service name (Optional)</Typography>
                    <Typography variant="caption" color="textSecondary" component="p">
                        If you already have Datadog set up, you can define a namespace under which Meltano logs arrive.
                    </Typography>
                    <TextField
                        data-testid="datadog-service"
                        fullWidth
                        variant="outlined"
                        label=""
                        value={service}
                        onChange={e => setService(e.target.value)}
                    />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                    <Button
                        data-testid="datadog-save-credential"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleSave}
                        disabled={isValidating}>
                        <Spinner active={isValidating}>
                            Save credential
                        </Spinner>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default AddDatadogCredentialModal
